import { useEffect, useRef, useState } from 'preact/hooks';
import { MoneyTag } from 'components/atoms/MoneyTag';
import { getAds, type GetAdsParams, adblockDetected } from 'service/ad/resource';
import type { NormalizedAdData, AdResponse } from 'service/ad/model.ts';
import { type OfferUnion } from 'utils/isOfferType';
import { OfferSkeleton } from 'components/offer/card/OfferSkeleton';
import { useIsVisible } from 'components/sspAdverts/useIsVisible';
import { cn } from 'utils/cn';
import type { AdvertZoneId } from 'components/sspAdverts/sspAdvertsConstants.ts';
import { getAdSection } from 'components/sspAdverts/section';
import { filters } from 'signals/filters';
import { useSignal } from '@preact/signals';

interface Props {
	id: string;
	advertZoneId: AdvertZoneId;
	offers: OfferUnion[];
	mainOffer?: OfferUnion;
}

const EXTENDED_VISIBILITY_TRACKING_ADS: NormalizedAdData['type'][] = ['DRTG', 'COMBINED'];

export function AdCard({ id, offers, advertZoneId, mainOffer }: Props) {
	const [advertResponse, setAdvertResponse] = useState<{ adData: NormalizedAdData; ad: AdResponse } | null>(null);
	const listRef = useRef(null);
	const isVisible = useIsVisible(id, '200px');
	const firstRender = useSignal(true);

	useEffect(() => {
		if (!isVisible.value || advertResponse) {
			return;
		}

		const requestParams: GetAdsParams = {
			id: id,
			zoneId: advertZoneId,
			width: 480,
			height: 480,
			options: {
				section: getAdSection(mainOffer, filters.peek()),
			},
			callback: (ad, adData) => {
				if (adData) {
					setAdvertResponse({ adData, ad });
				}
			},
		};

		getAds(requestParams);
	}, [offers, isVisible.value, mainOffer]);

	useEffect(() => {
		const resetIsAdLoaded = () => {
			setAdvertResponse(null);
		};

		document.addEventListener('astro:after-swap', resetIsAdLoaded);

		return () => {
			document.removeEventListener('astro:after-swap', resetIsAdLoaded);
		};
	}, []);

	const { ad, adData } = advertResponse ?? { ad: null, adData: null };

	if ((!adblockDetected.value || firstRender.peek()) && (!ad || !adData)) {
		firstRender.value = false;
		return (
			<li id={id} ref={listRef}>
				<OfferSkeleton dataTest="money-offer-skeleton" animate />
			</li>
		);
	}
	firstRender.value = false;
	const titleClassName = 'text-red line-clamp-2 overflow-ellipsis leading-6';

	// Hit external impress
	useEffect(() => {
		const externalImpressUrl = adData?.externalImpress;

		if (externalImpressUrl) {
			for (const impressUrl of externalImpressUrl) {
				const img = new Image();
				img.src = impressUrl;
			}
		}

		if (
			adData?.viewableImpressionUrl &&
			EXTENDED_VISIBILITY_TRACKING_ADS.includes(adData.type) &&
			globalThis?.window?.sssp?.extendVisibilityTracking
		) {
			globalThis?.window?.sssp?.extendVisibilityTracking([{ id, url: adData.viewableImpressionUrl }]);
		}
	}, [adData?.externalImpress]);

	if (adblockDetected.value) return null;

	return (
		<li id={id} ref={listRef}>
			{ad && adData && (
				<a href={adData.clickUrl}>
					<div class="offer-card @container group/card">
						<div class="offer-card__image-wrapper overflow-hidden rounded-2xl">
							<img
								src={adData.imageUrl}
								alt=""
								className={cn(
									'rounded-2xl group-hover/card:scale-105 transition-transform duration-500',
									adData.type === 'DRTG' && '!object-contain',
								)}
							/>
						</div>
						<div class="py-3 @cardLg:px-2 pb-0">
							<div class={cn(titleClassName, 'hidden @cardLg:block')}>{adData.title}</div>
							<div class={cn(titleClassName, '@cardLg:hidden')}>{adData.mobileTitle}</div>
							<div class="mt-1 line-clamp-1 overflow-ellipsis flex justify-between items-center flex-wrap gap-2">
								{adData.type === 'DRTG' ? (
									<div className="flex gap-1 truncate">
										<strong class="text-neutral-black shrink-0">{adData.price}</strong>{' '}
										<a
											href={adData.companyNameUrl}
											class="text-dark-blue-50 group-hover/card:text-dark-blue"
										>
											{adData.companyName}
										</a>
									</div>
								) : (
									<a href={adData.companyNameUrl} class="truncate">
										<strong class="text-neutral-black">{adData.companyName}</strong>
									</a>
								)}

								<div class="leading-0 shrink-0 grow text-right">
									<a href={ad.adInfoUrl}>
										{!adData.tag || adData.tag.toLocaleLowerCase() === 'reklama' ? (
											<MoneyTag className="h-[10px] md:h-3" />
										) : (
											<span class="text-dark-blue-50">{adData.tag}</span>
										)}
									</a>
								</div>
							</div>
						</div>
					</div>
				</a>
			)}
		</li>
	);
}
